import axios from "axios";
import { api } from "../../config";
import { tokenManager } from "../auth/token";

const axiosInstance = axios.create({
  baseURL: api.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response.data || response,
  (error) => {
    // Preserve the original error response structure
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx.
      // `error` in the response body can be either a plain string (older
      // endpoints) or { code, message, details } (the current envelope from
      // core.responses.error_response) — handle both rather than assuming.
      const rawError = error.response.data?.error;
      const rawErrorMessage = rawError && typeof rawError === 'object' ? rawError.message : rawError;

      const errorData = {
        message: rawErrorMessage ||
                 error.response.data?.message ||
                 error.response.data?.details ||
                 "An error occurred",
        status: error.response.status,
        data: error.response.data, // Preserve full response data
      };

      switch (error.response.status) {
        case 401:
          errorData.message = "Invalid credentials";
          tokenManager.clearAuth();
          break;
        case 404:
          errorData.message = rawErrorMessage || "Resource not found";
          break;
        case 500:
          errorData.message = rawErrorMessage ||
                             error.response.data?.details ||
                             "Internal Server Error";
          break;
        default:
          // Keep the message from errorData
          break;
      }

      // Create a new error with the preserved data
      const customError = new Error(errorData.message);
      customError.response = {
        status: errorData.status,
        data: errorData.data
      };
      return Promise.reject(customError);
    }

    // Network error or other issues
    return Promise.reject(error);
  }
);

export const setAuthorization = (token) => {
  if (token) {
    tokenManager.setToken(token);
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    tokenManager.clearAuth();
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

class APIBase {
  async get(url, params) {
    try {
      return await axiosInstance.get(url, params);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async get_pdf(url, params) {
    try {
      return await axiosInstance.get(url, {
        ...params,
        responseType: 'blob'
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post(url, data) {
    try {
      return await axiosInstance.post(url, data);
    } catch (error) {
      throw error;
    }
  }

  async put(url, data) {
    try {
      return await axiosInstance.put(url, data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async patch(url, data) {
    try {
      return await axiosInstance.patch(url, data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete(url, config = {}) {
    try {
      return await axiosInstance.delete(url, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async postFormData(url, data) {
    try {
      return await axiosInstance.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async postMail(url, data) {
    try {
      return await axiosInstance.post(url, data);
    } catch (error) {
      console.error("Mail API error:", error);
      return {
        success: false,
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message || "Mail sending failed"
      };
    }
  }

  async postUser(url, data) {
    try {
      const token = tokenManager.getToken();
      const headers = {
        "Content-Type": "application/json",
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${api.API_URL}${url}`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    console.error("API Error:", error);
    
    // If error has response data, extract the message
    if (error.response?.data) {
      const errorData = error.response.data;
      const message = errorData.error || 
                    errorData.message || 
                    errorData.details || 
                    "An error occurred";
      
      const customError = new Error(message);
      customError.response = error.response;
      customError.data = errorData;
      return customError;
    }
    
    // Otherwise return the original error
    return error;
  }
}

export const base = new APIBase();